import { useState } from 'react';
import { api, errMsg } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

export default function AgentExamplesImport({ agentId, value, onChange }) {
  const [file, setFile] = useState(null);
  const [skipHeader, setSkipHeader] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function inspect() {
    if (!file || busy) return;
    setError(''); setPreview(null); setBusy(true);
    try {
      if (file.size > 2 * 1024 * 1024) throw new Error('Le fichier dépasse 2 Mo.');
      const body = new FormData();
      body.append('file', file); body.append('skip_header', String(skipHeader));
      const { data } = await api.post(`/agents/${agentId}/examples/preview`, body);
      setPreview(data);
    } catch (e) { setError(errMsg(e)); }
    finally { setBusy(false); }
  }
  function add() {
    const list = [...(value || [])];
    const seen = new Set(list.map(e => JSON.stringify([e.q?.trim(), e.a?.trim()])));
    let added = 0;
    for (const item of preview.examples) {
      const key = JSON.stringify([item.q, item.a]);
      if (!seen.has(key)) { list.push(item); seen.add(key); added++; }
    }
    onChange(list); setPreview(null);
    toast.success(`${added} exemple(s) ajouté(s). Enregistrez le brouillon pour les conserver.`);
  }
  return <div className="rounded-lg border p-4 space-y-3" data-testid="agent-examples-import">
    <h3 className="font-semibold text-sm">Importer des exemples</h3>
    <p className="text-sm text-muted-foreground">CSV ou Excel (.xlsx), deux colonnes : question puis réponse attendue. Pour Excel, seule la première feuille est lue. Maximum : 2 Mo, 500 exemples.</p>
    <Input type="file" accept=".csv,.xlsx" aria-label="Fichier d’exemples à deux colonnes" disabled={busy}
      data-testid="examples-import-file" onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); setError(''); }} />
    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={skipHeader} disabled={busy}
      onChange={e => { setSkipHeader(e.target.checked); setPreview(null); setError(''); }} />La première ligne contient les titres des colonnes</label>
    <Button type="button" variant="outline" onClick={inspect} disabled={!file || busy} data-testid="examples-import-preview">{busy ? 'Lecture du fichier…' : 'Afficher l’aperçu'}</Button>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {preview && <div className="space-y-3">
      <p className="text-sm" role="status">{preview.count} exemple(s){preview.sheet ? ` · Feuille : ${preview.sheet}` : ''}{preview.ignored_empty_rows ? ` · ${preview.ignored_empty_rows} ligne(s) vide(s) ignorée(s)` : ''}</p>
      <div className="max-h-72 overflow-auto rounded border"><table className="w-full text-sm"><thead><tr className="border-b text-left"><th scope="col" className="p-2">Question</th><th scope="col" className="p-2">Réponse attendue</th></tr></thead><tbody>
        {preview.examples.map((e, i) => <tr key={i} className="border-b align-top"><td className="p-2 whitespace-pre-wrap break-words">{e.q}</td><td className="p-2 whitespace-pre-wrap break-words">{e.a}</td></tr>)}
      </tbody></table></div>
      <p className="text-xs text-muted-foreground">Les exemples seront ajoutés à ceux du brouillon. Les doublons identiques seront ignorés. Enregistrez ensuite le brouillon, puis publiez une version pour les utiliser en production.</p>
      <div className="flex flex-wrap gap-2"><Button type="button" onClick={add} data-testid="examples-import-add">Ajouter au brouillon</Button><Button type="button" variant="ghost" onClick={() => setPreview(null)}>Annuler</Button></div>
    </div>}
  </div>;
}
